import React, { useState, useEffect } from 'react';
import { formatNumber, formatDifference, formatRank } from '../../utils/formatting';
import { getValueColor, getRateColor, getRateDiffColor } from '../../utils/coloring';
import { sortData, requestSort, getSortIcon } from '../../utils/sorting';
import { getCrimeDisplayName } from '../../constants/crimes';
import { getStateAbbreviation } from '../../constants/locations';
import { useTableOverflow, useMenuState, useMobileState } from '../../hooks/tables';
import { ChevronDown } from 'lucide-react';
import './Tables.css';
import './LocationType.css';

const LocationTypeRatesTable = ({ 
  data,
  locationType,
  onLocationSelect,
  selectedLocation,
  selectedCrime,
  sortConfig,
  onSortChange,
  currentPage,
  itemsPerPage,
  compareMode,
  onCompareModeChange,
  rateViewMode,
  onRateViewModeChange,
  setHoveredLocation,
  onFilteredDataChange,
  populationGroup,
  onPopulationGroupChange
}) => {  
  const [populationMenuOpen, setPopulationMenuOpen] = useState(false);
  const { menuOpen, setMenuOpen, toggleMenu } = useMenuState();
  const isMobile = useMobileState();
  useTableOverflow();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.population-menu') && !event.target.closest('.population-menu-button')) {
        setPopulationMenuOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const togglePopulationMenu = () => {
    setPopulationMenuOpen(!populationMenuOpen);
  };
  
  useEffect(() => {
    const scrollTablesToRight = () => {
        const rateTables = document.querySelectorAll('.location-type-table.rates');
        
        const scrollTableRight = (table) => {
          const container = table.closest('.location-type-table-container') || table.parentElement;
          if (container) {
            container.scrollLeft = container.scrollWidth;
          }
        };
        
        rateTables.forEach(scrollTableRight);
    };
  
    const timer = setTimeout(scrollTablesToRight, 0);
    
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const urlTableSort = new URLSearchParams(window.location.search).get('table_sort');
    if (urlTableSort && sortConfig.key === urlTableSort) {
      return;
    }

    const currentKey = sortConfig.key;
    
    if (currentKey && (currentKey.includes('_rate') || currentKey.includes('_rate_diff'))) {
      let expectedKey;
      
      if (currentKey.includes('_rate_rank')) {
        expectedKey = rankField;
      } else if (currentKey.includes('_rate_diff_pct')) {
        expectedKey = rateDiffPctField;
      } else if (currentKey.includes('_rate_diff')) {
        expectedKey = rateDiffField;
      } else if (currentKey === 'population') {
        expectedKey = 'population';
      } else if (currentKey.includes('_rate')) {
        expectedKey = rateField;
      }
      
      if (expectedKey && currentKey !== expectedKey) {
        onSortChange({
          key: expectedKey,
          direction: sortConfig.direction
        });
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rateViewMode.rateScope, rateViewMode.rateType, sortConfig.key]);
  
  const isMetro = locationType === 'metro';
  const isCounty = locationType === 'county';
  const isCity = locationType === 'city';
  const isCountyOrCity = isCounty || isCity;
  const isOneCountyOrCity = data?.locations?.[0]?.total_counties_state === 1 || data?.locations?.[0]?.total_cities_state === 1; 
  const isAllCountiesNational = isCounty && selectedLocation?.info?.name === 'All Counties';
  const isAllCitiesNational = isCity && selectedLocation?.info?.name === 'All Cities';
  const isAllNational = isAllCountiesNational || isAllCitiesNational;
  const locationPrefix = locationType.charAt(0).toUpperCase() + locationType.slice(1);
  const crimePrefix = selectedCrime.toLowerCase();

  const isPeerJurisdictionRate =
    rateViewMode.rateType === 'city' || rateViewMode.rateType === 'county';
  
  let stateAbbr = '';
  if (isCountyOrCity && data?.locations?.length > 0) {
    stateAbbr = getStateAbbreviation(data.locations[0]?.state || '');
  }

  const POPULATION_GROUPS = [
    { value: 'all', label: 'All' },
    { value: 'under10k', label: '>10K' },
    { value: '10kplus', label: '10K+' },
    { value: '25kplus', label: '25K+' },
    { value: '50kplus', label: '50K+' },
    { value: '100kplus', label: '100K+' },
    { value: '250kplus', label: '250K+' },
    { value: '500kplus', label: '500K+' },
    { value: '1mplus', label: '1M+' }
  ];

  const METRO_POPULATION_GROUPS = [
    { value: 'all', label: 'All' },
    { value: 'under100k', label: '<100K' },
    { value: '100kplus', label: '100K+' },
    { value: '250kplus', label: '250K+' },
    { value: '500kplus', label: '500K+' },
    { value: '1mplus', label: '1M+' },
    { value: '2_5mplus', label: '2.5M+' },
    { value: '5mplus', label: '5M+' },
    { value: '10mplus', label: '10M+' }
  ];

  const checkPopulationMatch = (population, groupValue) => {
    if (!population || isNaN(population)) return false;
    
    switch (groupValue) {
      case 'under10k':
        return population < 10000;
      case 'under100k':
        return population < 100000;
      case '10kplus':
        return population >= 10000;
      case '25kplus':
        return population >= 25000;
      case '50kplus':
        return population >= 50000;
      case '100kplus':
        return population >= 100000;
      case '250kplus':
        return population >= 250000;
      case '500kplus':
        return population >= 500000;
      case '1mplus':
        return population >= 1000000;
      case '2_5mplus':
        return population >= 2500000;
      case '5mplus':
        return population >= 5000000;
      case '10mplus':
        return population >= 10000000;
      default:
        return true;
    }
  };

  const getAvailablePopulationGroups = () => {
    if (!data.locations || data.locations.length === 0) {
      return isMetro ? METRO_POPULATION_GROUPS : POPULATION_GROUPS;
    }
    
    const groups = isMetro ? METRO_POPULATION_GROUPS : POPULATION_GROUPS;
    return groups.filter(group => {
      if (group.value === 'all') return true;
      return data.locations.some(location => checkPopulationMatch(location.population, group.value));
    });
  };

  const getPopulationGroupLabel = () => {
    const groups = isMetro ? METRO_POPULATION_GROUPS : POPULATION_GROUPS;
    const group = groups.find(g => g.value === populationGroup);
    return group ? group.label : 'All';
  };

  const handlePopulationGroupChange = (newGroup) => {
    onPopulationGroupChange(newGroup);
    setPopulationMenuOpen(false);
  };

  const sortedData = sortData(data.locations, sortConfig);
  const filteredData = populationGroup === 'all' ? sortedData : sortedData.filter(location => 
    checkPopulationMatch(location.population, populationGroup)
  );

  const currentRows = filteredData.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  const handleSort = (key) => requestSort(key, sortConfig, onSortChange);

  useEffect(() => {
    if (onFilteredDataChange) {
      onFilteredDataChange({
        totalItems: filteredData.length,
        startItem: ((currentPage - 1) * itemsPerPage) + 1,
        endItem: Math.min(((currentPage - 1) * itemsPerPage) + itemsPerPage, filteredData.length)
      });
    }
  }, [filteredData.length, currentPage, itemsPerPage, onFilteredDataChange]);
  
  const handleRateViewModeChange = (type, scope) => {
    onRateViewModeChange({
      rateType: type,
      rateScope: scope
    });

    if (onCompareModeChange && scope !== compareMode) {
      onCompareModeChange(scope);
    }

    setMenuOpen(false);
    
    let newRateDiffField;
    
    if (isMetro) {
      newRateDiffField = type === 'metro' 
        ? `${crimePrefix}_all_metros_rate_diff`
        : `${crimePrefix}_national_rate_diff`;
    } else if (isCountyOrCity) {
      const isPeer = type === 'city' || type === 'county';
      if (isPeer) {
        newRateDiffField = scope === 'state'
          ? `${crimePrefix}_all_${locationType === 'county' ? 'counties' : 'cities'}_state_rate_diff`
          : `${crimePrefix}_all_${locationType === 'county' ? 'counties' : 'cities'}_national_rate_diff`;
      } else {
        newRateDiffField = scope === 'state'
          ? `${crimePrefix}_state_rate_diff`
          : `${crimePrefix}_national_rate_diff`;
      }
    } else {
      newRateDiffField = `${crimePrefix}_national_rate_diff`;
    }
    
    onSortChange({
      key: newRateDiffField,
      direction: 'desc'
    });
  };
  
  const getRateComparisonLabel = () => {
    if (isMetro) {
      return rateViewMode.rateType === 'metro' ? 'Metro' : 'National';
    }
    if (rateViewMode.rateType === locationType) {
      return rateViewMode.rateScope === 'state' ? `${stateAbbr} ${locationPrefix}` : `US ${locationPrefix}`;
    }
    if (rateViewMode.rateType === 'all_cities' || rateViewMode.rateType === 'all_counties') {
      return rateViewMode.rateScope === 'state' ? `${stateAbbr} State` : 'US National';
    }
    return rateViewMode.rateScope === 'state' ? `${stateAbbr} State` : 'US National';
  };

  const formatLocationName = (location) => {
    if (isCounty) {
      return (location.county === 'Carson City' && location.state === 'Nevada') ?
        `${location.county}, ${getStateAbbreviation(location.state)}` :
        location.state === 'Louisiana' ?
          `${location.county} Parish, ${getStateAbbreviation(location.state)}` :
        location.state === 'Alaska' ?
          `${location.county} Borough, ${getStateAbbreviation(location.state)}` :
          `${location.county} County, ${getStateAbbreviation(location.state)}`;
    } else if (isCity) {
      return `${location.city}, ${getStateAbbreviation(location.state)}`;
    } else {
      return location.name;
    }
  };

  const formatLocationNameForSelection = (location) => {
    if (isCounty) {
      return (location.county === 'Carson City' && location.state === 'Nevada') ?
        `${location.county}, ${location.state}` :
        location.state === 'Louisiana' ?
          `${location.county} Parish, ${location.state}` :
        location.state === 'Alaska' ?
          `${location.county} Borough, ${location.state}` :
          `${location.county} County, ${location.state}`;
    } else if (isCity) {
      return `${location.city}, ${location.state}`;
    } else {
      return location.name;
    }
  };

  const getLocationSelectData = (location) => {
    const locationName = formatLocationNameForSelection(location);
    
    return {
      name: locationName,
      type: locationType,
      info: {
        name: locationName,
        type: locationType,
        ...((isCounty || isCity) && { state: location.state })
      }
    };
  };

  const rateField = `${crimePrefix}_rate`;
  
  let avgRateField, rankField, rateDiffField, rateDiffPctField;
  
  if (isMetro) {
    if (rateViewMode.rateType === 'metro') {
      rankField = populationGroup === 'all' 
        ? `${crimePrefix}_national_rate_rank`
        : `${crimePrefix}_national_rate_rank_${populationGroup}`;
      avgRateField = `${crimePrefix}_all_metros_rate`;
      rateDiffField = `${crimePrefix}_all_metros_rate_diff`;
      rateDiffPctField = `${crimePrefix}_all_metros_rate_diff_pct`;
    } else {
      rankField = populationGroup === 'all' 
        ? `${crimePrefix}_national_rate_rank`
        : `${crimePrefix}_national_rate_rank_${populationGroup}`;
      avgRateField = `${crimePrefix}_national_rate`;
      rateDiffField = `${crimePrefix}_national_rate_diff`;
      rateDiffPctField = `${crimePrefix}_national_rate_diff_pct`;
    }
  } else if (isCountyOrCity) {
    if (isPeerJurisdictionRate) {
      const locationTypePlural = locationType === 'county' ? 'counties' : 'cities';
      if (rateViewMode.rateScope === 'state') {
        rankField = populationGroup === 'all'
          ? `${crimePrefix}_state_rate_rank`
          : `${crimePrefix}_state_rate_rank_${populationGroup}`;
        avgRateField = `${crimePrefix}_all_${locationTypePlural}_state_rate`;
        rateDiffField = `${crimePrefix}_all_${locationTypePlural}_state_rate_diff`;
        rateDiffPctField = `${crimePrefix}_all_${locationTypePlural}_state_rate_diff_pct`;
      } else {
        rankField = populationGroup === 'all'
          ? `${crimePrefix}_national_rate_rank`
          : `${crimePrefix}_national_rate_rank_${populationGroup}`;
        avgRateField = `${crimePrefix}_all_${locationTypePlural}_national_rate`;
        rateDiffField = `${crimePrefix}_all_${locationTypePlural}_national_rate_diff`;
        rateDiffPctField = `${crimePrefix}_all_${locationTypePlural}_national_rate_diff_pct`;
      }
    } else if (rateViewMode.rateScope === 'state') {
      rankField = populationGroup === 'all'
        ? `${crimePrefix}_state_rate_rank`
        : `${crimePrefix}_state_rate_rank_${populationGroup}`;
      avgRateField = `${crimePrefix}_state_rate`;
      rateDiffField = `${crimePrefix}_state_rate_diff`;
      rateDiffPctField = `${crimePrefix}_state_rate_diff_pct`;
    } else {
      rankField = populationGroup === 'all'
        ? `${crimePrefix}_national_rate_rank`
        : `${crimePrefix}_national_rate_rank_${populationGroup}`;
      avgRateField = `${crimePrefix}_national_rate`;
      rateDiffField = `${crimePrefix}_national_rate_diff`;
      rateDiffPctField = `${crimePrefix}_national_rate_diff_pct`;
    }
  } else {
    rankField = `${crimePrefix}_rate_rank`;
    avgRateField = `${crimePrefix}_national_rate`;
    rateDiffField = `${crimePrefix}_national_rate_diff`;
    rateDiffPctField = `${crimePrefix}_national_rate_diff_pct`;
  }

  return (
    <div className="main-table-wrapper">
      <div className="main-table-top">
        {(isCountyOrCity || isMetro) ? (
          <>
            <div className="population-filter-container">
              <div className="population-filter">
                <span>Population:</span>
                <span className="population-selected"><strong>{getPopulationGroupLabel()}</strong></span>
                <button 
                  className={`population-menu-button ${populationMenuOpen ? 'menu-open' : ''}`} 
                  onClick={togglePopulationMenu}
                >
                  <ChevronDown size={16} />
                </button>
                {populationMenuOpen && (
                  <div className="population-menu">
                    {getAvailablePopulationGroups().map(group => (
                      <button 
                        key={group.value}
                        className={populationGroup === group.value ? 'active' : ''}
                        onClick={() => handlePopulationGroupChange(group.value)}
                        disabled={populationGroup === group.value}
                      >
                        {group.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="comparison-container">
              <div className="comparison-menu-container">
                <button 
                  className={`comparison-menu-button ${menuOpen ? 'menu-open' : ''}`} 
                  onClick={toggleMenu}
                >
                  <ChevronDown size={16} />
                </button>
                {menuOpen && (
                  <div className="comparison-menu">
                    {isMetro ? (
                      <>
                        <button 
                          className={rateViewMode.rateType === 'metro' ? 'active' : ''}
                          onClick={() => handleRateViewModeChange('metro', 'national')}
                          disabled={rateViewMode.rateType === 'metro'}
                        >
                          Metro
                        </button>
                        <button 
                          className={rateViewMode.rateType === 'state' ? 'active' : ''}
                          onClick={() => handleRateViewModeChange('state', 'national')}
                          disabled={rateViewMode.rateType === 'state'}
                        >
                          National
                        </button>
                      </>
                    ) : (
                      <>
                        {!isAllNational && !isOneCountyOrCity && (
                          <>
                            <button 
                              className={rateViewMode.rateType === locationType && rateViewMode.rateScope === 'state' ? 'active' : ''}
                              onClick={() => handleRateViewModeChange(locationType, 'state')}
                              disabled={rateViewMode.rateType === locationType && rateViewMode.rateScope === 'state'}
                            >
                              {stateAbbr} {locationPrefix}
                            </button>
                            <button 
                              className={
                                (((isCounty ? rateViewMode.rateType === 'all_counties' : rateViewMode.rateType === 'all_cities') ||
                                  (rateViewMode.rateType === 'state' && rateViewMode.rateScope === 'state')) &&
                                  rateViewMode.rateScope === 'state')
                                  ? 'active'
                                  : ''
                              }
                              onClick={() =>
                                handleRateViewModeChange(isCounty ? 'all_counties' : 'all_cities', 'state')
                              }
                              disabled={
                                ((isCounty ? rateViewMode.rateType === 'all_counties' : rateViewMode.rateType === 'all_cities') &&
                                  rateViewMode.rateScope === 'state') ||
                                (rateViewMode.rateType === 'state' && rateViewMode.rateScope === 'state')
                              }
                            >
                              {stateAbbr} State
                            </button>
                          </>
                        )}
                        <button 
                          className={rateViewMode.rateType === locationType && rateViewMode.rateScope === 'national' ? 'active' : ''}
                          onClick={() => handleRateViewModeChange(locationType, 'national')}
                          disabled={rateViewMode.rateType === locationType && rateViewMode.rateScope === 'national'}
                        >
                          US {locationPrefix}
                        </button>
                        <button 
                          className={
                            (((isCounty ? rateViewMode.rateType === 'all_counties' : rateViewMode.rateType === 'all_cities') ||
                              (rateViewMode.rateType === 'national' && rateViewMode.rateScope === 'national')) &&
                              rateViewMode.rateScope === 'national')
                              ? 'active'
                              : ''
                          }
                          onClick={() =>
                            handleRateViewModeChange(isCounty ? 'all_counties' : 'all_cities', 'national')
                          }
                          disabled={
                            ((isCounty ? rateViewMode.rateType === 'all_counties' : rateViewMode.rateType === 'all_cities') &&
                              rateViewMode.rateScope === 'national') ||
                            (rateViewMode.rateType === 'national' && rateViewMode.rateScope === 'national')
                          }
                        >
                          US National
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
              <span>
                {getRateComparisonLabel()} {getCrimeDisplayName(selectedCrime)} Rate: <strong>{formatNumber(data.locations[0]?.[avgRateField])}</strong>
              </span>
            </div>
          </>
        ) : (
          <div>
            {isAllNational ? `US ${locationPrefix}` : 'National'} {getCrimeDisplayName(selectedCrime)} Rate: <strong>{formatNumber(data.locations[0]?.[avgRateField])}</strong>
          </div>
        )}
      </div>
      <div className="main-table-scroll-container">
        <table className={`main-table location-type-table ${(isCountyOrCity || isMetro) ? locationType : ''} rates`}>
          <thead>
            <tr>
              <th onClick={() => handleSort(rankField)} className="sortable">
                {isCountyOrCity && isMobile ? (
                  <>
                    <div>Rank</div>
                    {getSortIcon(rankField, sortConfig)}
                  </>
                ) : (
                  <div className="stacked-header">
                    <div>{isCountyOrCity ? getRateComparisonLabel() : locationPrefix}</div>
                    <div>Rank</div>
                    {getSortIcon(rankField, sortConfig)}
                  </div>
                )}
              </th>
              <th onClick={() => handleSort('name')} className="sortable">
                {locationPrefix}
                {getSortIcon('name', sortConfig)}
              </th>
              <th onClick={() => handleSort('population')} className="sortable">
                <div className="stacked-header">
                  <div>{locationPrefix}</div>
                  <div>Population</div>
                  {getSortIcon('population', sortConfig)}
                </div>
              </th>
              <th onClick={() => handleSort(rateField)} className="sortable">
                <div className="stacked-header">
                  <div>{getCrimeDisplayName(selectedCrime)}</div>
                  <div>Rate</div>
                  {getSortIcon(rateField, sortConfig)}
                </div>
              </th>
              <th onClick={() => handleSort(rateDiffField)} className="sortable">
                <div className="stacked-header">
                  <div>{(isCountyOrCity || isMetro) ? getRateComparisonLabel() : 'National'} Rate</div>
                  <div>Difference</div>
                  {getSortIcon(rateDiffField, sortConfig)}
                </div>
              </th>
              <th onClick={() => handleSort(rateDiffPctField)} className="sortable">
                <div className="stacked-header">
                  <div>% {(isCountyOrCity || isMetro) ? getRateComparisonLabel() : 'National'} Rate</div>
                  <div>Difference</div>
                  {getSortIcon(rateDiffPctField, sortConfig)}
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            {currentRows.map((location, index) => (
              <tr key={isCountyOrCity ? `${location[isCounty ? 'county' : 'city']}-${location.state}` : location.name || index}>
                <td className="rank-cell">{formatRank(location[rankField])}</td>
                <td>
                  <span 
                    onMouseEnter={() => setHoveredLocation(formatLocationNameForSelection(location))}
                    onMouseLeave={() => setHoveredLocation(null)}
                    onClick={() => onLocationSelect(getLocationSelectData(location))}
                  >
                    {formatLocationName(location)}
                  </span>
                </td>
                <td className="number-cell">{formatNumber(location.population)}</td>
                <td className="number-cell" style={{ color: getRateColor(location[rateField], location[avgRateField]) }}>
                  {formatNumber(location[rateField])}
                </td>
                <td className="number-cell bold" style={{ color: getRateDiffColor(location[rateDiffField]) }}>
                  {formatDifference(location[rateDiffField], false, true)}
                </td>
                <td className="number-cell" style={{ color: getValueColor(location[rateDiffPctField]) }}>
                  {formatDifference(location[rateDiffPctField], true)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default LocationTypeRatesTable;